import { useMutation } from '@apollo/client';
import React from 'react';

import {
  eluveAdminHasuraContext,
  useCompleteFragment,
} from '@eluve/apollo-client';
import { Switch, useToast } from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

export const tenantTemplateBundleFragment = graphql(`
  fragment TenantTemplateBundle on TenantTemplateBundles @_unmask {
    __typename
    tenantId
    llmModelPromptTemplateId
    isEnabled
    createdAt
    updatedAt
  }
`);

export const setTenantTemplateBundleMutation = graphql(
  `
    mutation setTenantTemplateBundle(
      $tenantId: uuid!
      $llmModelPromptTemplateId: uuid!
      $isEnabled: Boolean!
    ) {
      insertTenantTemplateBundlesOne(
        object: {
          tenantId: $tenantId
          llmModelPromptTemplateId: $llmModelPromptTemplateId
          isEnabled: $isEnabled
        }
        onConflict: {
          constraint: tenant_template_bundles_pkey
          updateColumns: [isEnabled]
        }
      ) {
        ...TenantTemplateBundle
      }
    }
  `,
  [tenantTemplateBundleFragment],
);

export interface TenantTemplateBundleToggleProps {
  tenantId: string;
  llmModelPromptTemplateId: string;
  displayName: string;
  onRequestDisable?: () => void;
}

export const TenantTemplateBundleToggle: React.FC<
  TenantTemplateBundleToggleProps
> = ({ tenantId, llmModelPromptTemplateId, displayName, onRequestDisable }) => {
  const toast = useToast();
  const [updateTenantTemplateBundle] = useMutation(
    setTenantTemplateBundleMutation,
    {
      refetchQueries: ['getTenantTemplateBundles'],
      onError: () =>
        toast.error(
          `Failed to update access to template bundle ${displayName}`,
        ),
      optimisticResponse: (data) => ({
        insertTenantTemplateBundlesOne: {
          __typename: 'TenantTemplateBundles' as const,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          ...data,
        },
      }),
    },
  );

  const submit = async (isEnabled: boolean) => {
    await updateTenantTemplateBundle({
      context: eluveAdminHasuraContext,
      variables: {
        tenantId,
        llmModelPromptTemplateId,
        isEnabled,
      },
    });
  };

  const data = useCompleteFragment({
    fragment: tenantTemplateBundleFragment,
    key: {
      llmModelPromptTemplateId,
      tenantId,
    },
    strict: false,
  });

  return (
    <Switch
      size="sm"
      onCheckedChange={(isChecked) => {
        if (!isChecked && onRequestDisable) {
          onRequestDisable();
        } else {
          submit(isChecked);
        }
      }}
      checked={data?.isEnabled ?? false}
    />
  );
};
